import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Users, TrendingUp, Award, Play, Sun } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [featuredVideo, setFeaturedVideo] = useState(null);

  useEffect(() => {
    const fetchFeaturedVideo = async () => {
      try {
        const response = await axios.get(`${API}/featured-video`);
        if (response.data) {
          setFeaturedVideo(response.data);
        }
      } catch (error) {
        console.error('Error fetching featured video:', error);
      }
    };
    fetchFeaturedVideo();
  }, []);

  return (
    <div className="page-content">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="display-large">Pacific Ghost Baseball</h1>
          <p className="body-large hero-subtitle">Elite Baseball Development & College Placement</p>
          <div className="hero-buttons">
            <Link to="/contact" className="btn-primary">Join Our Team</Link>
            <Link to="/showcase" className="btn-secondary">View Showcases</Link>
          </div>
          {/* Summer HS Program Button */}
          <div style={{ marginTop: '20px' }}>
            <Link 
              to="/player/login"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '14px 32px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: '#000',
                fontWeight: 700,
                fontSize: '1rem',
                textDecoration: 'none',
                borderRadius: '8px',
                boxShadow: '0 4px 16px rgba(245, 158, 11, 0.4)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 24px rgba(245, 158, 11, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 16px rgba(245, 158, 11, 0.4)';
              }}
              data-testid="join-summer-hs-program-btn"
            >
              <Sun size={20} />
              JOIN SUMMER HS PROGRAM
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Video Section */}
      {featuredVideo && (
        <section className="featured-video-section" style={{
          padding: 'var(--spacing-xl) var(--spacing-medium)',
          background: 'linear-gradient(180deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)'
        }}>
          <div className="container">
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <h2 className="heading-1" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                <Play size={32} style={{ color: 'var(--brand-primary)' }} />
                {featuredVideo.title || 'Featured Video'}
              </h2>
            </div>
            <div style={{
              maxWidth: '900px',
              margin: '0 auto',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              border: '2px solid var(--brand-primary)'
            }}>
              <video
                data-testid="featured-video"
                src={featuredVideo.video_url}
                poster={featuredVideo.thumbnail_url}
                controls
                autoPlay
                muted
                loop
                playsInline
                style={{
                  width: '100%',
                  display: 'block',
                  aspectRatio: '16/9',
                  objectFit: 'cover',
                  background: '#000'
                }}
              />
            </div>
          </div>
        </section>
      )}

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card animated fadeIn">
              <Trophy className="stat-icon" size={48} />
              <h3 className="display-medium">50+</h3>
              <p className="body-medium">Championships Won</p>
            </div>
            <div className="stat-card animated fadeIn delay-200ms">
              <Users className="stat-icon" size={48} />
              <h3 className="display-medium">200+</h3>
              <p className="body-medium">Active Players</p>
            </div>
            <div className="stat-card animated fadeIn delay-500ms">
              <TrendingUp className="stat-icon" size={48} />
              <h3 className="display-medium">85%</h3>
              <p className="body-medium">College Placement Rate</p>
            </div>
            <div className="stat-card animated fadeIn delay-500ms">
              <Award className="stat-icon" size={48} />
              <h3 className="display-medium">30+</h3>
              <p className="body-medium">College Commits</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="container">
          <div className="about-content">
            <h2 className="heading-1">Building Champions On and Off the Field</h2>
            <p className="body-large">
              Pacific Ghost Baseball is committed to developing elite baseball talent through 
              professional coaching, competitive tournaments, and comprehensive college placement support. 
              Our program emphasizes skill development, character building, and academic excellence.
            </p>
            <div className="feature-grid">
              <div className="network-card">
                <h3 className="network-card-title">Elite Coaching</h3>
                <p className="network-card-content">
                  Learn from experienced coaches with professional and collegiate backgrounds.
                </p>
              </div>
              <div className="network-card">
                <h3 className="network-card-title">College Recruiting</h3>
                <p className="network-card-content">
                  Dedicated college advisors to guide players through the recruitment process.
                </p>
              </div>
              <div className="network-card">
                <h3 className="network-card-title">Competitive Play</h3>
                <p className="network-card-content">
                  Compete in top-tier tournaments including Perfect Game showcases.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-1">Ready to Take Your Game to the Next Level?</h2>
            <p className="body-large">Join the Pacific Ghost family and start your journey to college baseball.</p>
            <Link to="/contact" className="btn-cta">Get Started Today</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
